import React, { useCallback, useEffect, useState } from 'react';

import { UserEntity } from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import alertToast from 'Util/ToastifyUtils';
import { store } from 'Models/Store';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { getCargoTypes, CargoTypeForVehicleDto } from 'Services/Api/_HumanWritten/UserVehicleService';
import { CustomerCargoProfileDto, getCargoProfiles } from 'Services/Api/_HumanWritten/CustomerCargoProfileService';
import { CargoProfileCard } from './CargoProfileCard';
import { openCargoProfileCreateModal, openCargoProfileEditModal } from './CargoProfileModal';

interface CargoProfilesSectionProps {
	user?: UserEntity;
}

/**
 * Staff-only section (below the vehicle list) listing a customer's cargo profiles. A profile pre-defines
 * the cargo characteristics for one cargo type; when a booking for this customer selects that cargo type,
 * the profile's characteristics are applied automatically. One profile per cargo type. Styled to match
 * the vehicles list above it: header + add button, then a card per profile with Edit / Delete.
 */
function CargoProfilesSection({ user }: CargoProfilesSectionProps) {
	const [profiles, setProfiles] = useState<CustomerCargoProfileDto[]>([]);
	const [cargoTypes, setCargoTypes] = useState<CargoTypeForVehicleDto[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const load = useCallback(async () => {
		if (!isNotNullOrUndefined(user)) {
			return;
		}
		setIsLoading(true);
		try {
			const [loadedCargoTypes, loadedProfiles] = await Promise.all([getCargoTypes(), getCargoProfiles(user.id)]);
			setCargoTypes(loadedCargoTypes);
			setProfiles(loadedProfiles);
		} catch (error) {
			console.error('Failed to load cargo profiles:', error);
			alertToast('Failed to load cargo profiles', 'error', 'Error');
		} finally {
			setIsLoading(false);
		}
	}, [user?.id]);

	useEffect(() => {
		load();
	}, [load]);

	const cargoTypeLabel = useCallback(
		(cargoTypeId: string) => {
			const match = cargoTypes.find(x => x.id === cargoTypeId);
			return match ? `${match.cargoMake ?? ''} ${match.cargoModel ?? ''}`.trim() : 'Unknown cargo type';
		},
		[cargoTypes],
	);

	const handleAdd = () => {
		if (!user) {
			return;
		}
		openCargoProfileCreateModal(
			user.id,
			profiles.map(p => p.cargoTypeId),
			load,
		).catch(() => {
			// Modal was cancelled
		});
	};

	const handleEdit = (profile: CustomerCargoProfileDto) => {
		if (!user) {
			return;
		}
		openCargoProfileEditModal(user.id, profile, load).catch(() => {
			// Modal was cancelled
		});
	};

	return (
		<div className="cargo-profiles-section mt-6">
			<div className="fake-table__header">
				Cargo profiles
				{/* Manager and above only — see CargoProfileCard. */}
				{store.isManager && (
					<Button
						icon={{ icon: 'plus', iconPos: 'icon-only' }}
						display={Display.Outline}
						colors={Colors.Secondary}
						onClick={handleAdd}
						labelVisible={false}
						className="fake-table__header-add-btn"
						newStyles
						buttonProps={{ 'data-test-id': 'cargo-profile-add-button' } as any}
					/>
				)}
			</div>

			{isLoading && (
				<div className="vehicles-list__loading">
					<LottieSpinner />
				</div>
			)}

			{!isLoading && profiles.length === 0 && (
				<div className="vehicles-list__empty">
					<p>No cargo profiles saved to this account.</p>
				</div>
			)}

			{!isLoading && profiles.length > 0 && (
				<div className="vehicles-list">
					{profiles.map(profile => (
						<CargoProfileCard
							key={profile.id}
							profile={profile}
							cargoTypeLabel={cargoTypeLabel(profile.cargoTypeId)}
							onEdit={() => handleEdit(profile)}
							onRemove={load}
						/>
					))}
				</div>
			)}
		</div>
	);
}

export default CargoProfilesSection;
